import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Shield, Mail, Lock, ArrowRight, Check, Link2 } from "lucide-react";
import { toast } from "sonner";

import collaboratorApi, { clearCollaboratorToken, setCollaboratorToken } from "../../lib/collaboratorApi";
import { sanitizeCollaboratorStorage } from "./collaboratorAccess";

function safeErrorMessage(error, fallback) {
  if (error?.response?.status === 401) return "Email ou mot de passe incorrect.";
  if (error?.response?.status === 403) return "Cet accès collaborateur n’est plus actif.";
  if (error?.response?.status === 404) return "Invitation introuvable.";
  if (error?.response?.status === 410) return "Cette invitation a expiré.";
  return fallback;
}

export default function CollaboratorLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get("token") || "";

  const [step, setStep] = useState(invitationToken ? "create_password" : "email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [invitationData, setInvitationData] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(Boolean(invitationToken));
  const [backendError, setBackendError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    clearCollaboratorToken();
    sanitizeCollaboratorStorage();
  }, []);

  useEffect(() => {
    if (!invitationToken) return;

    let ignore = false;
    setLoadingInvitation(true);
    setBackendError("");
    collaboratorApi
      .get("/collaborator-auth/invitation", { params: { token: invitationToken } })
      .then((response) => {
        if (ignore) return;
        setInvitationData(response.data);
        setEmail(response.data.email || "");
        setStep("create_password");
      })
      .catch((error) => {
        if (ignore) return;
        setBackendError(safeErrorMessage(error, "Impossible de vérifier cette invitation."));
      })
      .finally(() => {
        if (!ignore) setLoadingInvitation(false);
      });

    return () => {
      ignore = true;
    };
  }, [invitationToken]);

  async function handleEmailSubmit(event) {
    event.preventDefault();
    if (!email.trim()) return;
    setStep("password");
  }

  async function handleCreatePassword(event) {
    event.preventDefault();
    if (!invitationToken) {
      toast.error("Lien d’invitation manquant.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await collaboratorApi.post("/collaborator-auth/accept-invitation", {
        token: invitationToken,
        password,
      });
      setCollaboratorToken(response.data.token);
      toast.success("Accès collaborateur activé.");
      navigate("/collaborator-hub");
    } catch (error) {
      toast.error(safeErrorMessage(error, "Impossible d’activer cet accès."));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await collaboratorApi.post("/collaborator-auth/login", {
        email,
        password,
      });
      setCollaboratorToken(response.data.token);
      toast.success("Connexion réussie.");
      navigate("/collaborator-hub");
    } catch (error) {
      toast.error(safeErrorMessage(error, "Impossible de vous connecter."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-offwhite flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-md rounded-2xl border border-white/50 p-8 shadow-xl animate-in fade-in zoom-in duration-500">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-sage/20">
            <Shield className="w-8 h-8 text-sage" />
          </div>
          <h2 className="font-serif text-3xl text-charcoal">Espace Collaborateur</h2>
          <p className="text-sm text-charcoal-soft mt-2">Accès sécurisé pour les partenaires du mariage.</p>
        </div>

        {backendError ? (
          <div className="mb-6 rounded-xl border border-red-100 bg-red-50/80 p-4 text-sm text-red-700">{backendError}</div>
        ) : null}

        {loadingInvitation ? (
          <div className="rounded-xl border border-border-cream bg-white/50 p-6 text-sm text-charcoal-soft text-center">
            Vérification de votre invitation sécurisée…
          </div>
        ) : null}

        {!loadingInvitation && step === "email" ? (
          <form onSubmit={handleEmailSubmit} className="space-y-6 animate-in slide-in-from-right-4" data-testid="collaborator-email-step">
            <div>
              <label className="overline text-[10px] text-charcoal-soft font-bold block mb-2">Votre email d’invitation</label>
              <div className="relative">
                <Mail className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-soft" />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="prestataire@example.com"
                  className="w-full pl-12 pr-4 py-3 bg-white/50 border border-border-cream rounded-xl focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage transition-all text-charcoal"
                  autoComplete="email"
                  data-testid="collaborator-email"
                  required
                />
              </div>
            </div>
            <button type="submit" className="w-full btn-sage py-4 rounded-xl text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2">
              Continuer <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        ) : null}

        {!loadingInvitation && step === "create_password" ? (
          <form onSubmit={handleCreatePassword} className="space-y-6 animate-in slide-in-from-right-4" data-testid="collaborator-create-password-step">
            <div className="p-4 bg-sage/5 border border-sage/20 rounded-xl mb-6">
              <p className="text-sm text-charcoal text-center">
                Bienvenue <strong>{invitationData?.name || email}</strong> !
                <br />
                Définissez votre mot de passe sécurisé pour activer cet accès.
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-charcoal-soft mt-3 flex items-center justify-center gap-2">
                <Link2 className="w-3.5 h-3.5" />
                Lien à usage unique
              </p>
            </div>
            <div>
              <label className="overline text-[10px] text-charcoal-soft font-bold block mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-soft" />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/50 border border-border-cream rounded-xl focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage transition-all text-charcoal"
                  autoComplete="new-password"
                  data-testid="collaborator-password-create"
                  required
                />
              </div>
            </div>
            <div>
              <label className="overline text-[10px] text-charcoal-soft font-bold block mb-2">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-soft" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/50 border border-border-cream rounded-xl focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage transition-all text-charcoal"
                  autoComplete="new-password"
                  data-testid="collaborator-password-confirm"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-sage py-4 rounded-xl text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60"
            >
              Créer mon accès <Check className="w-4 h-4" />
            </button>
          </form>
        ) : null}

        {!loadingInvitation && step === "password" ? (
          <form onSubmit={handleLogin} className="space-y-6 animate-in slide-in-from-right-4" data-testid="collaborator-password-step">
            <div className="text-center mb-6">
              <p className="text-sm text-charcoal">Connexion sécurisée pour <strong>{email}</strong></p>
            </div>
            <div>
              <label className="overline text-[10px] text-charcoal-soft font-bold block mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-soft" />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/50 border border-border-cream rounded-xl focus:border-sage focus:outline-none focus:ring-1 focus:ring-sage transition-all text-charcoal"
                  autoComplete="current-password"
                  data-testid="collaborator-password-login"
                  required
                />
              </div>
              <div className="flex justify-end mt-2">
                <a href="/collaborator-forgot-password" onClick={(e) => { e.preventDefault(); navigate("/collaborator-forgot-password"); }} className="text-[10px] uppercase tracking-widest text-charcoal hover:text-sage transition-colors">
                  Mot de passe oublié ?
                </a>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-sage py-4 rounded-xl text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-60"
            >
              Se connecter <ArrowRight className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setStep("email")} className="w-full text-xs font-bold text-charcoal-soft hover:text-charcoal uppercase tracking-widest transition-colors">
              Utiliser un autre email
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
